import type {PluralFormsModel} from '@/types/aem';
import {formatString} from '@/utils/stringUtils';

/**
 * Resolves a plural form template from AEM content based on count and locale.
 *
 * Uses the browser's built-in `Intl.PluralRules` for locale-aware plural
 * resolution (covers CLDR rules for en/ru/uk/pl/ar/ja/zh/ko/etc.).
 *
 * `Intl.PluralRules` never selects the `zero` category for languages like
 * English (count 0 → `other`), so an explicit override maps count `0` to the
 * `zero` form when provided. Falls back to the `other` form when the selected
 * form is missing or `null`.
 */
export const selectPluralForm = (
    count: number,
    forms: PluralFormsModel | undefined | null,
    locale: string
): string | undefined => {
    if (!forms) return undefined;

    if (count === 0 && forms.zero != null) return forms.zero;

    const rule = new Intl.PluralRules(locale).select(count);
    const template = forms[rule as keyof PluralFormsModel] ?? forms.other;

    return template ?? undefined;
};

/**
 * Formats a count against locale-aware plural forms.
 *
 * Example:
 *   formatPlural(3, { one: "{0} action remaining", other: "{0} actions remaining" }, "en")
 *   → "3 actions remaining"
 */
export const formatPlural = (
    count: number,
    forms: PluralFormsModel | undefined | null,
    locale: string
): string | undefined => {
    const template = selectPluralForm(count, forms, locale);

    if (template === undefined) return undefined;

    return formatString(template, count);
};

/** Injects a plural-formatted count into notification description's `{0}` placeholder. */
export const formatNotificationWithPlural = <T extends {description?: string}>(
    notification: T | undefined,
    count: number,
    forms: PluralFormsModel | undefined,
    locale: string
): T | undefined => {
    if (!notification?.description) return notification;
    const pluralText = formatPlural(count, forms, locale);
    if (!pluralText) return notification;
    return {...notification, description: formatString(notification.description, pluralText)};
};
